/**
 * Pages narratives Regles, Aventure, Univers.
 *
 * Renderers specialises par type de structure detecte dans le JSON.
 */

import { html, nothing } from 'lit';
import { loadGlossaire, loadRegles } from '../data/loaders';

// -- Helpers --

function keyToTitle(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isStepArray(
  arr: unknown[],
): arr is { numero?: number; titre: string; description: string }[] {
  return (
    arr.length > 0 &&
    typeof arr[0] === 'object' &&
    arr[0] !== null &&
    'titre' in arr[0] &&
    'description' in arr[0]
  );
}

function isTableArray(arr: unknown[]): arr is Record<string, unknown>[] {
  return (
    arr.length > 0 &&
    typeof arr[0] === 'object' &&
    arr[0] !== null &&
    !('description' in arr[0] && 'titre' in arr[0])
  );
}

function renderSteps(steps: { numero?: number; titre: string; description: string }[]): unknown {
  return html`
    <div class="content-steps">
      ${steps.map(
        (s) => html`
        <div class="content-step">
          ${s.numero != null ? html`<span class="content-step-num">${s.numero}</span>` : nothing}
          <div class="content-step-body">
            <strong class="content-step-title">${s.titre}</strong>
            <p class="content-step-desc">${s.description}</p>
          </div>
        </div>
      `,
      )}
    </div>
  `;
}

function renderTable(rows: Record<string, unknown>[]): unknown {
  if (rows.length === 0) return nothing;
  const first = rows[0];
  if (!first) return nothing;
  const keys = Object.keys(first);
  return html`
    <div class="content-table-wrap">
      <table class="content-table">
        <thead>
          <tr>${keys.map((k) => html`<th>${keyToTitle(k)}</th>`)}</tr>
        </thead>
        <tbody>
          ${rows.map(
            (row) => html`
            <tr>${keys.map((k) => html`<td>${String(row[k] ?? '-')}</td>`)}</tr>
          `,
          )}
        </tbody>
      </table>
    </div>
  `;
}

function renderValue(value: unknown, depth: number): unknown {
  if (value == null) return nothing;

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return html`<p class="content-paragraph">${String(value)}</p>`;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return nothing;
    if (typeof value[0] === 'string' || typeof value[0] === 'number') {
      return html`<ul class="content-list">
        ${value.map((item) => html`<li>${String(item)}</li>`)}
      </ul>`;
    }
    if (isStepArray(value)) {
      return renderSteps(value);
    }
    if (isTableArray(value)) {
      return renderTable(value as Record<string, unknown>[]);
    }
    return html`<ul class="content-list">
      ${value.map((item) => html`<li>${renderValue(item, depth + 1)}</li>`)}
    </ul>`;
  }

  if (typeof value === 'object') {
    return renderObject(value as Record<string, unknown>, depth);
  }

  return nothing;
}

function renderObject(obj: Record<string, unknown>, depth: number): unknown {
  if (depth > 4) {
    return html`<pre class="content-raw">${JSON.stringify(obj, null, 2)}</pre>`;
  }
  const entries = Object.entries(obj);
  return html`${entries.map(([key, val]) => {
    const title = keyToTitle(key);
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      return html`<div class="content-kv">
        <span class="content-kv-key">${title}</span>
        <span class="content-kv-val">${String(val)}</span>
      </div>`;
    }
    if (depth === 0)
      return html`<h2 class="content-h2">${title}</h2>${renderValue(val, depth + 1)}`;
    if (depth === 1)
      return html`<h3 class="content-h3">${title}</h3>${renderValue(val, depth + 1)}`;
    if (depth === 2)
      return html`<h4 class="content-h4">${title}</h4>${renderValue(val, depth + 1)}`;
    return html`<div class="content-nested">
      <strong class="content-nested-label">${title}</strong>
      ${renderValue(val, depth + 1)}
    </div>`;
  })}`;
}

function renderSection(title: string, data: unknown): unknown {
  if (data == null) return nothing;
  return html`
    <section class="content-section">
      <h2 class="content-h2">${title}</h2>
      ${renderValue(data, 1)}
    </section>
  `;
}

// -- Pages publiques --

export async function renderReglesPage(): Promise<unknown> {
  const regles = await loadRegles();
  return html`
    <div class="content-page">
      <header class="content-header">
        <h1 class="content-title" data-morph data-morph-duration="1500">Regles</h1>
        <p class="content-intro">Reference complete des mecaniques de jeu.</p>
      </header>
      ${renderSection('Regles de base', regles.regles_de_base)}
      ${renderSection('Combat', regles.combat)}
      ${renderSection('Magie', regles.magie)}
      ${renderSection('Caracteristiques', regles.caracteristiques)}
      ${renderSection('Etats', regles.etats)}
      ${renderSection('Progression', regles.progression)}
    </div>
  `;
}

export async function renderAventurePage(): Promise<unknown> {
  const regles = await loadRegles();
  return html`
    <div class="content-page">
      <header class="content-header">
        <h1 class="content-title" data-morph data-morph-duration="1500">Aventure</h1>
        <p class="content-intro">Exploration, voyages et dangers du monde.</p>
      </header>
      ${renderSection('Ordre de marche', regles.aventure?.ordre_de_marche)}
      ${renderSection('Voyages', regles.aventure?.voyages)}
      ${renderSection('Dangers de voyage', regles.aventure?.dangers_voyage)}
      ${renderSection('Autres dangers', regles.aventure?.autres_dangers)}
      ${renderSection("Enquete et recherche d'indices", regles.aventure?.enquete_recherche_indices)}
      ${renderSection('Repos court', regles.aventure?.repos_court)}
      ${renderSection('Repos long', regles.aventure?.repos_long)}
    </div>
  `;
}

export async function renderUniversPage(): Promise<unknown> {
  const glossaire = await loadGlossaire();
  const parCategorie = new Map<string, Array<{ terme: string; definition: string }>>();
  for (const entry of glossaire.termes) {
    const cat = entry.categorie;
    if (!parCategorie.has(cat)) parCategorie.set(cat, []);
    parCategorie.get(cat)?.push({ terme: entry.terme, definition: entry.definition });
  }

  return html`
    <div class="content-page">
      <header class="content-header">
        <h1 class="content-title" data-morph data-morph-duration="1500">Univers - Terres d'Osgild</h1>
        <p class="content-intro">${glossaire.termes.length} termes classes par categorie.</p>
      </header>
      ${[...parCategorie.entries()].map(
        ([cat, termes]) => html`
        <section class="content-section">
          <h2 class="content-h2">${keyToTitle(cat)}</h2>
          <dl class="content-dl">
            ${termes.map(
              (t) => html`
              <div class="content-dl-entry">
                <dt class="content-dt">${t.terme}</dt>
                <dd class="content-dd">${t.definition}</dd>
              </div>
            `,
            )}
          </dl>
        </section>
      `,
      )}
    </div>
  `;
}
